import { useState, useEffect } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  TextInput,
  Button,
  SimpleGrid,
  Divider,
  Loader,
  Center,
  Alert,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
import {
  IconBuilding,
  IconCheck,
  IconAlertCircle,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { useCompany, useUpdateCompany } from '../../hooks/useCompany';
import { formatPhoneNumber, formatEin } from '../../utils/format';

interface CompanyAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface Company {
  id: string;
  name: string;
  legalName: string;
  ein: string;
  address: CompanyAddress;
  phone: string;
  website: string;
}

export default function CompanyPage() {
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: queryData, loading: isLoading } = useCompany();
  const companyData = queryData as { company?: Company } | undefined;
  const company = companyData?.company;

  const form = useForm({
    initialValues: {
      name: '',
      legalName: '',
      ein: '',
      phone: '',
      website: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
    },
  });

  // Populate form when data loads (in effect to avoid infinite render loop)
  useEffect(() => {
    if (company && !form.isTouched()) {
      form.setValues({
        name: company.name || '',
        legalName: company.legalName || '',
        ein: company.ein || '',
        phone: company.phone || '',
        website: company.website || '',
        addressLine1: company.address?.line1 || '',
        addressLine2: company.address?.line2 || '',
        city: company.address?.city || '',
        state: company.address?.state || '',
        zip: company.address?.zip || '',
        country: company.address?.country || 'US',
      });
    }
  }, [company, form]);

  const updateCompanyHook = useUpdateCompany();
  const updateLoading = updateCompanyHook.isPending;

  const handleSubmit = form.onSubmit((values) => {
    setSaved(false);
    setError(null);
    updateCompanyHook.mutate(
      {
        input: {
          name: values.name,
          legalName: values.legalName,
          ein: values.ein,
          phone: values.phone,
          website: values.website,
          address: {
            line1: values.addressLine1,
            line2: values.addressLine2,
            city: values.city,
            state: values.state,
            zip: values.zip,
            country: values.country,
          },
        },
      },
      {
        onSuccess: () => {
          setSaved(true);
          setError(null);
        },
        onError: (err: any) => {
          setError(err.message || 'Failed to update company settings');
          setSaved(false);
        },
      },
    );
  });

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Company"
        subtitle="Manage company settings and information"
        breadcrumbs={[
          { label: 'Company', to: '/admin/workforce/company' },
          { label: 'Settings' },
        ]}
      />

      {saved && (
        <Alert icon={<IconCheck size={16} />} color="green" title="Saved">
          Company settings updated successfully.
        </Alert>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Paper withBorder p="lg" radius="md">
            <Group gap="sm" mb="md">
              <IconBuilding size={20} />
              <Text fw={600} size="lg">
                General Information
              </Text>
            </Group>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <TextInput
                label="Company Name"
                placeholder="Enter company name"
                required
                {...form.getInputProps('name')}
              />
              <TextInput
                label="Legal Name"
                placeholder="Enter legal entity name"
                {...form.getInputProps('legalName')}
              />
              <TextInput
                label="EIN"
                placeholder="XX-XXXXXXX"
                {...form.getInputProps('ein')}
              />
              <TextInput
                label="Phone"
                placeholder="(XXX) XXX-XXXX"
                {...form.getInputProps('phone')}
              />
              <TextInput
                label="Website"
                placeholder="https://example.com"
                {...form.getInputProps('website')}
              />
            </SimpleGrid>
          </Paper>

          <Paper withBorder p="lg" radius="md">
            <Text fw={600} size="lg" mb="md">
              Address
            </Text>

            <Stack gap="md">
              <TextInput
                label="Address Line 1"
                placeholder="Street address"
                required
                {...form.getInputProps('addressLine1')}
              />
              <TextInput
                label="Address Line 2"
                placeholder="Suite, unit, etc. (optional)"
                {...form.getInputProps('addressLine2')}
              />
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                <TextInput
                  label="City"
                  placeholder="City"
                  required
                  {...form.getInputProps('city')}
                />
                <TextInput
                  label="State"
                  placeholder="State code (e.g., CA)"
                  required
                  {...form.getInputProps('state')}
                />
                <TextInput
                  label="ZIP Code"
                  placeholder="XXXXX"
                  required
                  {...form.getInputProps('zip')}
                />
              </SimpleGrid>
              <TextInput
                label="Country"
                placeholder="Country code"
                {...form.getInputProps('country')}
                w={200}
              />
            </Stack>
          </Paper>

          <Group justify="flex-end">
            <Button variant="default" onClick={() => form.reset()}>
              Reset
            </Button>
            <Button type="submit" loading={updateLoading}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  );
}
